import { StatusBar, StatusBarStyle } from 'expo-status-bar'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { ScrollView, StyleSheet, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useFocusEffect, useRouter } from 'expo-router'
import { useMutation, useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { BottomSheetModal } from '@gorhom/bottom-sheet'
import {
  BADGES,
  BADGE_CATEGORY_LABELS,
  BADGE_CATEGORY_ORDER,
  BADGE_IDS,
  tierName,
  type BadgeCategory,
  type TierName
} from '@ppwr/convex/badges/catalog'
import { TIER_PALETTES } from '../../../assets/badges/palettes'

import { MainHeader } from '../../components/MainHeader/MainHeader'
import { DS } from '../../design-system'
import { useStore } from '../../models'
import { events } from '../../models/analytics/analytics-events'
import { badgeDisplayState, type BadgeDisplayState, type BadgeProgressRow } from './badgeDisplay'
import { BadgeGridItem } from './BadgeGridItem'
import { BadgeDetailSheet } from './BadgeDetailSheet'

const TIER_BREAKDOWN_ORDER = ['Bronze', 'Silver', 'Gold', 'Platinum'] as const satisfies readonly TierName[]

interface TierBreakdownItemProps {
  tier: TierName
  count: number
}

const TierBreakdownItem: React.FC<TierBreakdownItemProps> = ({ tier, count }) => (
  <View style={styles.tierItem}>
    <View style={styles.tierCount}>
      <View style={[styles.tierDot, { backgroundColor: TIER_PALETTES[tier].primary }]} />
      <DS.Text variant="Body Small/Medium">{count.toString()}</DS.Text>
    </View>
    <DS.Text variant="Caption/Regular" color="Brand/Dark Grey" textAlign="center">
      {tier}
    </DS.Text>
  </View>
)

export const BadgesScreen = observer(() => {
  const router = useRouter()
  const {
    analytics: { setEvent },
    session: { isAdmin, isSuperAdmin }
  } = useStore()
  const myBadges = useQuery(api.badges.queries.getMyBadges, {})
  const resetBadge = useMutation(api.badges.mutations.resetMyBadgeForTesting)
  const resetBadgeDiamonds = useMutation(api.diamonds.mutations.resetMyBadgeDiamondsForTesting)
  const sheetRef = useRef<BottomSheetModal>(null)
  const [selected, setSelected] = useState<BadgeDisplayState | null>(null)

  useFocusEffect(
    useCallback(() => {
      setEvent(events.badges.page_viewed, {})
    }, [setEvent])
  )

  const rowsByBadgeId = useMemo(() => {
    const map = new Map<string, BadgeProgressRow>()
    for (const row of myBadges ?? []) map.set(row.badgeId, row)
    return map
  }, [myBadges])

  const sections = useMemo(() => {
    const byCategory = new Map<BadgeCategory, BadgeDisplayState[]>()
    for (const id of BADGE_IDS) {
      const badge = BADGES[id]
      const states = byCategory.get(badge.category) ?? []
      states.push(badgeDisplayState(badge, rowsByBadgeId.get(id)))
      byCategory.set(badge.category, states)
    }
    // Categories render in catalog order; empty ones (reserved slots) are skipped.
    return BADGE_CATEGORY_ORDER.filter((category) => byCategory.has(category)).map((category) => ({
      category,
      label: BADGE_CATEGORY_LABELS[category],
      states: byCategory.get(category) ?? []
    }))
  }, [rowsByBadgeId])

  const earnedByTier = useMemo(() => {
    const counts: Record<TierName, number> = { Bronze: 0, Silver: 0, Gold: 0, Platinum: 0 }

    for (const section of sections) {
      for (const state of section.states) {
        if (state.isEarned) counts[tierName(state.badge, state.tier)] += 1
      }
    }

    return counts
  }, [sections])

  const onPressBadge = useCallback(
    (state: BadgeDisplayState) => {
      setEvent(events.badges.detail_opened, { badgeId: state.badge.id, earned: state.isEarned, tier: state.tier })
      setSelected(state)
      sheetRef.current?.present()
    },
    [setEvent]
  )

  const onResetBadge = useCallback(
    (state: BadgeDisplayState) => {
      // Reset the badge, then the diamonds it paid, so re-earning it pays again
      resetBadge({ badgeId: state.badge.id })
        .then(() => resetBadgeDiamonds({ badgeId: state.badge.id }))
        .catch((err) => console.error('badge reset failed', err))
    },
    [resetBadge, resetBadgeDiamonds]
  )

  /*admin*/
  const showAdminControls = isAdmin || isSuperAdmin

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar animated={true} style={'dark' as StatusBarStyle} hidden={false} />
      <MainHeader
        leftPress={() => router.back()}
        title="Badges"
        titleStyle={{ color: DS.palette['Global/Black'], marginLeft: DS.spacing[12] }}
      />
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.webWrapper}>
          <DS.Frame gap="24">
            <View style={styles.tierBreakdown}>
              {TIER_BREAKDOWN_ORDER.map((tier) => (
                <TierBreakdownItem key={tier} tier={tier} count={earnedByTier[tier]} />
              ))}
            </View>
            {sections.map((section) => (
              <DS.Frame key={section.category} gap="12">
                <DS.Text variant="Caption/Bold" uppercase>
                  {section.label}
                </DS.Text>
                <View style={styles.grid}>
                  {section.states.map((state) => (
                    <BadgeGridItem
                      key={state.badge.id}
                      state={state}
                      onPress={onPressBadge}
                      onReset={showAdminControls ? onResetBadge : undefined}
                    />
                  ))}
                </View>
              </DS.Frame>
            ))}
          </DS.Frame>
        </View>
      </ScrollView>
      <BadgeDetailSheet ref={sheetRef} state={selected} />
    </SafeAreaView>
  )
})

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream']
  },
  scrollContent: {
    padding: DS.spacing[24],
    paddingBottom: DS.spacing[48]
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start'
  },
  tierBreakdown: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: DS.spacing[8],
    flexDirection: 'row',
    paddingVertical: DS.spacing[12]
  },
  tierItem: {
    alignItems: 'center',
    flex: 1,
    gap: DS.spacing[4]
  },
  tierCount: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: DS.spacing[4]
  },
  tierDot: {
    borderRadius: DS.spacing[6],
    height: DS.spacing[12],
    width: DS.spacing[12]
  },
  webWrapper: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center'
  }
})
